import React, { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import { Calendar, dateFnsLocalizer, Navigate } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { useMediaQuery } from "@mui/material";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import { useApi } from "../../utils/api";
import AddWorkoutDetails from "../AddWorkoutDetails";
import WorkoutCalendarView from "./WorkoutCalendarView";
import ManageProductsDialog from "./ManageProductsDialog";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Paper,
  IconButton,
  Box,
  Typography,
  Drawer,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import AssignProgramDialog from "./AssignProgramDialog";
import UploadDocumentModal from "./UploadDocumentModal";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import LoadingButton from "@mui/lab/LoadingButton";
import { useUserInfo } from "../../utils/user";
import "react-big-calendar/lib/css/react-big-calendar.css";
import enUS from "date-fns/locale/en-US";
import History from "../ClientPortal/History";
const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const DragAndDropCalendar = withDragAndDrop(Calendar);
const AUTO_REVIEW_MODES = {
  WEIGHT_PROGRESS: "WEIGHT_PROGRESS",
  RPE_TARGET: "RPE_TARGET",
};

// Update theme to include button styles and primary color
const theme = createTheme({
  typography: {
    fontFamily: '"Roboto Mono", monospace',
  },
  palette: {
    primary: {
      main: "#ff0000", // Red color to match the rest of the app
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Roboto Mono", monospace',
        },
      },
    },
  },
});

// Update calendar styles for two-week view
const calendarStyles = {
  height: "calc(100vh - 100px)",
  fontFamily: '"Roboto Mono", monospace !important',
  "& .rbc-month-view": {
    flexBasis: "auto !important",
    minHeight: "400px",
    height: "auto",
  },
  "& .rbc-month-row": {
    minHeight: "200px !important",
  },
  "& .rbc-header": {
    fontFamily: '"Roboto Mono", monospace',
    padding: "8px 0",
  },
  "& .rbc-button-link": {
    fontFamily: '"Roboto Mono", monospace',
  },
  "& .rbc-toolbar button": {
    fontFamily: '"Roboto Mono", monospace',
  },
  "& .rbc-row-segment": {
    padding: "0 2px",
  },
  // Hide rows beyond the first two weeks
  "& .rbc-month-row:nth-of-type(n+3)": {
    display: "none",
  },
  // Hide the last row of the month header (the 6th row)
  "& .rbc-month-header .rbc-row:last-child": {
    display: "none",
  },
};

// Custom toolbar with copy/move helper text on the right
const CalendarToolbar = ({ label, onNavigate, localizer: { messages } }) => (
  <div
    className="rbc-toolbar"
    style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}
  >
    <span className="rbc-btn-group">
      <button type="button" onClick={() => onNavigate(Navigate.PREVIOUS)}>
        {messages.previous}
      </button>
      <button type="button" onClick={() => onNavigate(Navigate.TODAY)}>
        {messages.today}
      </button>
      <button type="button" onClick={() => onNavigate(Navigate.NEXT)}>
        {messages.next}
      </button>
    </span>
    <span className="rbc-toolbar-label">{label}</span>
    <Typography
      variant="caption"
      sx={{
        marginLeft: "auto",
        color: "text.secondary",
      }}
    >
      <b>Copy:</b> Option (Alt) + click workout(s) <b>Move:</b> Shift + click
      workout(s)
    </Typography>
  </div>
);

function workoutProgramMeta(workout) {
  const length = parseInt(workout?.programLength, 10);
  if (!Number.isFinite(length) || length <= 1) return null;
  const week = parseInt(workout?.currentWeek, 10);
  const currentWeek = Number.isFinite(week) && week > 0 ? week : 1;
  return {
    currentWeek,
    programLength: length,
    percent: Math.min(100, (currentWeek / length) * 100),
  };
}

const WorkoutProgramProgress = ({ currentWeek, programLength, percent, inverted }) => (
  <Box sx={{ mt: 0.5 }}>
    <Typography
      variant="caption"
      noWrap
      sx={{
        display: "block",
        fontSize: "0.65rem",
        lineHeight: 1.2,
        opacity: inverted ? 0.85 : 0.9,
      }}
    >
      Week {currentWeek} of {programLength}
    </Typography>
    <Box
      sx={{
        mt: 0.25,
        height: 3,
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: inverted ? "action.hover" : "rgba(255,255,255,0.3)",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: `${percent}%`,
          borderRadius: 1,
          bgcolor: inverted ? "primary.main" : "rgba(255,255,255,0.95)",
          transition: "width 0.2s ease",
        }}
      />
    </Box>
  </Box>
);

// Custom day component for inline editing
const DayComponent = ({
  date,
  workouts = [],
  onSelectEvent,
  onSelectSlot,
  selectedWorkoutIds = new Set(),
  selectionMode = null,
}) => {
  return (
    <Box
      sx={{
        cursor: "pointer",
        p: 0.25,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelectSlot({
          start: date,
          end: date,
          isNew: true,
        });
      }}
    >
      {workouts.map((workout, i) => {
        const isSelected = selectedWorkoutIds.has(workout.id);
        const inCopyMode = selectionMode === "copy";
        const inMoveMode = selectionMode === "move";
        const selectedForCopy = inCopyMode && isSelected;
        const selectedForMove = inMoveMode && isSelected;
        const borderColor =
          selectedForCopy || selectedForMove ? "primary.main" : "transparent";
        const borderWidth = selectedForCopy || selectedForMove ? 2 : 0;
        const programMeta = workoutProgramMeta(workout);
        return (
          <Paper
            key={workout.id || i}
            sx={{
              py: 1,
              px: 0.5,
              mb: 0,
              minHeight: "auto !important",
              cursor: "pointer",
              ...(selectedForCopy || selectedForMove
                ? {
                    bgcolor: "white",
                    color: "text.primary",
                    border: `${borderWidth}px solid`,
                    borderColor,
                  }
                : {
                    bgcolor: workout.completed
                      ? "success.light"
                      : "primary.main",
                    color: "white",
                    outline: "none",
                  }),
              transition:
                "background-color 0.15s, outline 0.15s, border-color 0.15s",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSelectEvent(workout, e);
            }}
          >
            <Typography variant="body2" noWrap>
              {workout.name || workout.title}
            </Typography>
            {workout.description ? (
              <Typography
                variant="caption"
                noWrap
                sx={{ display: "block", opacity: 0.8 }}
              >
                {workout.description}
              </Typography>
            ) : null}
            {programMeta ? (
              <WorkoutProgramProgress
                {...programMeta}
                inverted={selectedForCopy || selectedForMove}
              />
            ) : null}
          </Paper>
        );
      })}
    </Box>
  );
};

const ClientWorkoutCalendar = ({
  clientId,
  notificationWorkoutParams = null,
  onNotificationWorkoutConsumed,
  onClientDeleted,
  onClientUpdated,
  setHeaderActions,
  onProductsSaved,
  /** Called debounced with this calendar's user id when workouts may have changed for the current week. */
  onWorkoutsChanged,
}) => {
  const { id: userId, stripeIsSetup } = useUserInfo();
  const isViewingSelf = userId === clientId;
  const [workouts, setWorkouts] = useState([]);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [client, setClient] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [repeatLoading, setRepeatLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignPrograms, setAssignPrograms] = useState([]);
  const [assignTemplates, setAssignTemplates] = useState([]);
  const [assignProgramId, setAssignProgramId] = useState("");
  const [assignStartDate, setAssignStartDate] = useState(moment());
  const [assignDaySelections, setAssignDaySelections] = useState({});
  const [assignSaving, setAssignSaving] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [editUserNameValue, setEditUserNameValue] = useState("");
  const [editUserWeightUnit, setEditUserWeightUnit] = useState("lbs");
  const [editUserAutoReviewMode, setEditUserAutoReviewMode] = useState(
    AUTO_REVIEW_MODES.WEIGHT_PROGRESS
  );
  const [editUserSaving, setEditUserSaving] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD")
  );
  const [exportEndDate, setExportEndDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [selectedWorkoutIds, setSelectedWorkoutIds] = useState(new Set());
  const [selectedWorkoutsForCopy, setSelectedWorkoutsForCopy] = useState([]);
  const [selectionMode, setSelectionMode] = useState(null); // 'move' | 'copy'
  const mobile = useMediaQuery("(max-width: 600px)");
  const [selectedHistoryExercise, setSelectedHistoryExercise] = useState(null);
  const [notificationExerciseFocus, setNotificationExerciseFocus] =
    useState(null);
  const consumedNotificationKeyRef = useRef(null);
  const saveButtonId = "add-workout-save-button";
  const {
    getWorkout,
    getWorkoutsForUser,
    saveWorkoutDetails,
    addWorkoutForUser,
    getActivity,
    startConversation,
    getUser,
    deleteUser,
    patchUser,
    getPrograms,
    getWorkoutTemplates,
  } = useApi();
  const auth = getAuth();
  const workoutsChangedTimerRef = useRef(null);

  const scheduleWeekSummaryRefresh = useCallback(
    (forUserId) => {
      if (!onWorkoutsChanged || !forUserId) return;
      clearTimeout(workoutsChangedTimerRef.current);
      workoutsChangedTimerRef.current = setTimeout(() => {
        onWorkoutsChanged(forUserId);
      }, 350);
    },
    [onWorkoutsChanged]
  );

  useEffect(() => {
    return () => clearTimeout(workoutsChangedTimerRef.current);
  }, []);

  // Refs to track the current clientId for each API call to prevent stale updates
  const currentWorkoutsClientIdRef = useRef(null);
  const currentActivityClientIdRef = useRef(null);
  const currentClientClientIdRef = useRef(null);

  // Track whether the Shift key is held for copy-on-drop behaviour
  const shiftKeyHeldRef = useRef(false);

  // Modify the dateRange initialization to check localStorage
  const [dateRange, setDateRange] = useState(() => {
    const savedDate = localStorage.getItem(`calendar-date-${clientId}`);
    if (savedDate) {
      const date = moment(savedDate);
      return {
        start: date.startOf("month"),
        end: date.endOf("month"),
      };
    }
    return {
      start: moment().startOf("month"),
      end: moment().endOf("month"),
    };
  });

  useEffect(() => {
    if (clientId) {
      if (selectionMode === "move") {
        setSelectedWorkoutIds(new Set());
        setSelectionMode(null);
      }
      // Reset refs when clientId changes to cancel previous requests
      currentWorkoutsClientIdRef.current = clientId;
      currentActivityClientIdRef.current = clientId;
      currentClientClientIdRef.current = clientId;

      fetchWorkouts(null, { notifyWeekSummary: false });
      fetchClient();
    }
  }, [clientId, days]);

  useEffect(() => {
    if (!notificationWorkoutParams?.workoutId) {
      consumedNotificationKeyRef.current = null;
    }
  }, [notificationWorkoutParams]);

  // Open workout drawer from notification deep link (?workoutId=…&exerciseIndex=…)
  useEffect(() => {
    if (!notificationWorkoutParams?.workoutId) return;
    const { workoutId, exerciseIndex, openComments, queryClientId } =
      notificationWorkoutParams;
    if (queryClientId && queryClientId !== clientId) return;

    const key = `${workoutId}-${exerciseIndex}-${openComments ? "c" : ""}`;
    if (consumedNotificationKeyRef.current === key) return;

    let cancelled = false;
    (async () => {
      try {
        const w = await getWorkout(workoutId);
        if (cancelled || currentWorkoutsClientIdRef.current !== clientId || !w)
          return;

        consumedNotificationKeyRef.current = key;
        const exIdx = Math.max(
          0,
          parseInt(exerciseIndex ?? "0", 10) || 0
        );
        const formattedWorkout = {
          ...w,
          id: w.id,
          name: w.name,
          title: w.name,
          description: w.description,
          exercises: w.exercises || [],
          date: w.date ? moment.unix(w.date) : moment(),
        };
        setHasUnsavedChanges(false);
        setSelectedWorkout(formattedWorkout);
        setDrawerOpen(true);
        setNotificationExerciseFocus({
          index: exIdx,
          openComments: !!openComments,
        });
        onNotificationWorkoutConsumed?.();
      } catch (err) {
        console.error("Failed to open workout from notification:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    notificationWorkoutParams,
    getWorkout,
    onNotificationWorkoutConsumed,
  ]);

  const fetchClient = async () => {
    const requestClientId = clientId;
    try {
      const response = await getUser(requestClientId);
      // Only update state if this is still the current client
      if (currentClientClientIdRef.current === requestClientId) {
        setClient(response);
      }
    } catch (error) {
      // Only log error if this is still the current client
      if (currentClientClientIdRef.current === requestClientId) {
        console.error("Error fetching client:", error);
      }
    }
  };

  const fetchWorkouts = async (
    customDateRange = null,
    { notifyWeekSummary = true } = {}
  ) => {
    const requestClientId = clientId;
    try {
      const range = customDateRange || dateRange;
      const response = await getWorkoutsForUser(
        requestClientId,
        moment(range.start).subtract(1, "month").unix(),
        moment(range.end).add(1, "month").unix()
      );

      // Only update state if this is still the current client
      if (currentWorkoutsClientIdRef.current === requestClientId) {
        const formattedWorkouts = response.map((workout) => ({
          ...workout,
          title: workout.name,
          start: moment.unix(workout.date).startOf("day").toDate(),
          end: moment.unix(workout.date).endOf("day").toDate(),
          exercises: workout.exercises || [],
        }));

        setWorkouts(formattedWorkouts);

        if (notifyWeekSummary) {
          const apiStart = moment(range.start)
            .subtract(1, "month")
            .startOf("day");
          const apiEnd = moment(range.end).add(1, "month").endOf("day");
          if (moment().isBetween(apiStart, apiEnd, "day", "[]")) {
            scheduleWeekSummaryRefresh(requestClientId);
          }
        }
      }
    } catch (error) {
      // Only log error if this is still the current client
      if (currentWorkoutsClientIdRef.current === requestClientId) {
        console.error("Error fetching workouts:", error);
      }
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Shift") shiftKeyHeldRef.current = true;
    };
    const onKeyUp = (e) => {
      if (e.key === "Shift") shiftKeyHeldRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  const handleEventDrop = async ({ event, start }) => {
    const newDate = moment(start).startOf("day").unix();
    const currentDate = moment(event.start).startOf("day").unix();
    const isCopy = shiftKeyHeldRef.current;

    // Skip move when dropped on the same day (copy to same day is allowed)
    if (!isCopy && newDate === currentDate) return;

    if (isCopy) {
      // Copy: create a new workout on the target date
      const { id, title, start: _s, end: _e, ...rest } = event;
      try {
        await addWorkoutForUser(clientId, { ...rest, date: newDate });
        await fetchWorkouts();
      } catch (error) {
        console.error("Error copying workout:", error);
      }
    } else {
      // Move: optimistically update date, then persist
      setWorkouts((prev) =>
        prev.map((w) =>
          w.id === event.id
            ? {
                ...w,
                date: newDate,
                start: moment(start).startOf("day").toDate(),
                end: moment(start).endOf("day").toDate(),
              }
            : w
        )
      );
      try {
        await saveWorkoutDetails(event.id, { date: newDate });
        await fetchWorkouts();
      } catch (error) {
        console.error("Error moving workout:", error);
        await fetchWorkouts(); // revert optimistic update
      }
    }
  };

  const handleSelectEvent = (workout, e) => {
    const isShift = e?.shiftKey ?? false;
    const isAlt = e?.altKey ?? false;

    if (isShift || isAlt) {
      // Determine mode: alt (Option) = copy, shift-only = move
      const mode = isAlt ? "copy" : "move";

      if (selectionMode && selectionMode !== mode) {
        setSelectedWorkoutIds(new Set([workout.id]));
        setSelectedWorkoutsForCopy(mode === "copy" ? [workout] : []);
      } else {
        setSelectedWorkoutIds((prev) => {
          const next = new Set(prev);
          if (next.has(workout.id)) {
            next.delete(workout.id);
          } else {
            next.add(workout.id);
          }
          return next;
        });

        if (mode === "copy") {
          setSelectedWorkoutsForCopy((prev) => {
            if (prev.some((selected) => selected.id === workout.id)) {
              return prev.filter((selected) => selected.id !== workout.id);
            }
            return [...prev, workout];
          });
        } else {
          setSelectedWorkoutsForCopy([]);
        }
      }
      setSelectionMode(mode);
      return;
    }

    // Regular click: clear any selection and open drawer
    if (selectedWorkoutIds.size > 0) {
      setSelectedWorkoutIds(new Set());
      setSelectionMode(null);
      setSelectedWorkoutsForCopy([]);
      return;
    }

    setHasUnsavedChanges(false);
    const formattedWorkout = {
      ...workout,
      id: workout.id,
      name: workout.title || workout.name,
      description: workout.description,
      exercises: workout.exercises || [],
      date: workout.date ? moment.unix(workout.date) : moment(workout.start),
    };
    setSelectedWorkout(formattedWorkout);
    setDrawerOpen(true);
  };

  const placeSelectedWorkouts = async (targetDate) => {
    const selected =
      selectionMode === "copy" && selectedWorkoutsForCopy.length > 0
        ? selectedWorkoutsForCopy
        : workouts.filter((w) => selectedWorkoutIds.has(w.id));
    if (selected.length === 0) return;

    // Sort ascending by original date
    const sorted = [...selected].sort((a, b) => {
      const da = a.date ? moment.unix(a.date) : moment(a.start);
      const db = b.date ? moment.unix(b.date) : moment(b.start);
      return da.valueOf() - db.valueOf();
    });

    const earliestMoment = sorted[0].date
      ? moment.unix(sorted[0].date)
      : moment(sorted[0].start);
    const targetMoment = moment(targetDate).startOf("day");

    try {
      if (selectionMode === "copy") {
        await Promise.all(
          sorted.map((workout) => {
            const originalMoment = workout.date
              ? moment.unix(workout.date)
              : moment(workout.start);
            const offsetDays = originalMoment.diff(earliestMoment, "days");
            const newDate = targetMoment.clone().add(offsetDays, "days");
            return addWorkoutForUser(clientId, {
              name: workout.title || workout.name,
              description: workout.description,
              exercises: workout.exercises || [],
              date: newDate.unix(),
              completed: false,
              currentWeek: workout.currentWeek ?? 1,
              programLength: workout.programLength ?? 12,
            });
          })
        );
      } else {
        await Promise.all(
          sorted.map((workout) => {
            const originalMoment = workout.date
              ? moment.unix(workout.date)
              : moment(workout.start);
            const offsetDays = originalMoment.diff(earliestMoment, "days");
            const newDate = targetMoment.clone().add(offsetDays, "days");
            return saveWorkoutDetails(workout.id, {
              name: workout.title || workout.name,
              description: workout.description,
              exercises: workout.exercises || [],
              date: newDate.unix(),
              completed: workout.completed,
            });
          })
        );
      }
    } catch (error) {
      console.error("Error placing workouts:", error);
    }

    setSelectedWorkoutIds(new Set());
    setSelectionMode(null);
    setSelectedWorkoutsForCopy([]);
    await fetchWorkouts();
  };

  const handleSelectSlot = ({ start }) => {
    if (selectedWorkoutIds.size > 0) {
      placeSelectedWorkouts(start);
      return;
    }
    setHasUnsavedChanges(false);
    setSelectedWorkout({
      name: "",
      description: "",
      exercises: [],
      date: moment(start),
    });
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedWorkout(null);
    setHasUnsavedChanges(false);
    setNotificationExerciseFocus(null);
  };

  const handleAttemptCloseDrawer = () => {
    if (hasUnsavedChanges) {
      setUnsavedDialogOpen(true);
      return;
    }
    handleCloseDrawer();
  };

  const handleConfirmSave = () => {
    const saveButton = document.getElementById(saveButtonId);
    if (saveButton) {
      saveButton.click();
    } else {
      // If the button cannot be found, fall back to closing to avoid trapping the user
      handleCloseDrawer();
    }
    setUnsavedDialogOpen(false);
  };

  const handleDiscardChanges = () => {
    setUnsavedDialogOpen(false);
    handleCloseDrawer();
  };

  const handleCancelClose = () => {
    setUnsavedDialogOpen(false);
  };

  const handleSaveWorkout = async (workoutData) => {
    try {
      console.log("Saving workout:", workoutData); // Debug log
      if (selectedWorkout?.id) {
        await saveWorkoutDetails(selectedWorkout.id, workoutData);
      } else {
        await addWorkoutForUser(clientId, workoutData);
      }
      await fetchWorkouts();
      handleCloseDrawer();
    } catch (error) {
      console.error("Error saving workout:", error);
    }
  };

  const handleNavigate = (date) => {
    const start = moment(date).startOf("month");
    const end = moment(date).endOf("month");

    // Save the current date to localStorage
    localStorage.setItem(`calendar-date-${clientId}`, moment(date).format());

    const newDateRange = {
      start,
      end,
    };

    setDateRange(newDateRange);

    // Fetch workouts for the new date range
    fetchWorkouts(newDateRange);
  };

  // Add cleanup effect to remove localStorage item when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem(`calendar-date-${clientId}`);
    };
  }, [clientId]);

  // Clear selection on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && selectedWorkoutIds.size > 0) {
        setSelectedWorkoutIds(new Set());
        setSelectionMode(null);
        setSelectedWorkoutsForCopy([]);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedWorkoutIds]);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenEditUser = () => {
    setEditUserNameValue(client?.name ?? "");
    setEditUserWeightUnit(client?.weightUnit === "kg" ? "kg" : "lbs");
    setEditUserAutoReviewMode(
      client?.autoReviewMode === AUTO_REVIEW_MODES.RPE_TARGET
        ? AUTO_REVIEW_MODES.RPE_TARGET
        : AUTO_REVIEW_MODES.WEIGHT_PROGRESS
    );
    setEditUserModalOpen(true);
    handleCloseMenu();
  };

  const handleSaveEditUser = async () => {
    if (!editUserNameValue.trim()) return;
    setEditUserSaving(true);
    try {
      await patchUser(clientId, {
        name: editUserNameValue.trim(),
        weightUnit: editUserWeightUnit === "kg" ? "kg" : "lbs",
        autoReviewMode: editUserAutoReviewMode,
      });
      setClient((prev) =>
        prev
          ? {
              ...prev,
              name: editUserNameValue.trim(),
              weightUnit: editUserWeightUnit === "kg" ? "kg" : "lbs",
              autoReviewMode: editUserAutoReviewMode,
            }
          : null
      );
      setEditUserModalOpen(false);
      onClientUpdated?.({
        name: editUserNameValue.trim(),
        weightUnit: editUserWeightUnit === "kg" ? "kg" : "lbs",
        autoReviewMode: editUserAutoReviewMode,
      });
    } catch (err) {
      console.error("Failed to update client name:", err);
    } finally {
      setEditUserSaving(false);
    }
  };

  const handleRepeatLastWeek = async () => {
    try {
      setRepeatLoading(true);
      const response = await getWorkoutsForUser(
        clientId,
        moment().subtract(12, "month").unix(),
        moment().add(12, "month").unix()
      );

      if (!response.length) {
        alert("No workouts found to repeat");
        return;
      }

      const formattedWorkouts = response.map((workout) => ({
        ...workout,
        start: moment.unix(workout.date).startOf("day").toDate(),
      }));

      const sortedWorkouts = formattedWorkouts.sort(
        (a, b) => moment(b.start).valueOf() - moment(a.start).valueOf()
      );

      const mostRecentDate = moment(sortedWorkouts[0].start);
      const mostRecentWeekWorkouts = sortedWorkouts.filter((workout) =>
        moment(workout.start).isSame(mostRecentDate, "week")
      );

      const newWorkouts = mostRecentWeekWorkouts.map((workout) => ({
        ...workout,
        date: moment(workout.start).add(7, "days").unix(),
        exercises: workout.exercises.map((exercise) => {
          const newExercise = { ...exercise };
          // Clear values based on set type
          newExercise.sets = exercise.sets.map((set) => {
            const newSet = { ...set };
            // Clear weight for RPE sets
            if (set.type === "RPE") {
              delete newSet.weight;
            }
            // Clear RPE for weight-based sets
            if (set.type === "WEIGHT") {
              delete newSet.rpe;
            }
            return newSet;
          });
          return newExercise;
        }),
      }));

      await Promise.all(
        newWorkouts.map((workout) => addWorkoutForUser(clientId, workout))
      );

      handleCloseMenu();
      fetchWorkouts();
    } catch (error) {
      console.error("Error repeating last week's workouts:", error);
      alert("Failed to repeat last week's workouts. Please try again.");
    } finally {
      setRepeatLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    // Prevent coaches from deleting themselves
    if (isViewingSelf) {
      alert("You cannot delete your own account.");
      return;
    }
    try {
      setDeleteLoading(true);
      await deleteUser(clientId);
      handleCloseMenu();
      if (onClientDeleted) {
        onClientDeleted();
      }
    } catch (error) {
      console.error("Failed to delete client:", error);
      alert("Failed to remove client. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setResetPasswordLoading(true);
      if (client?.email) {
        await sendPasswordResetEmail(auth, client.email);
        handleCloseMenu();
      }
    } catch (error) {
      console.error("Failed to send password reset email:", error);
      alert("Failed to send password reset email. Please try again.");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleCloseConfirmDelete = () => {
    setConfirmDeleteOpen(false);
  };

  const handleOpenProductsDialog = () => {
    handleCloseMenu();
    setProductsDialogOpen(true);
  };

  const handleCloseProductsDialog = () => {
    setProductsDialogOpen(false);
  };

  const handleOpenDocumentsDialog = () => {
    handleCloseMenu();
    setDocumentsDialogOpen(true);
  };

  const handleCloseDocumentsDialog = () => {
    setDocumentsDialogOpen(false);
  };

  const csvEscape = (value) => {
    if (value == null) return "";
    const stringValue = String(value).replace(/"/g, '""');
    return /[",\n]/.test(stringValue) ? `"${stringValue}"` : stringValue;
  };

  const handleOpenExportDialog = () => {
    handleCloseMenu();
    setExportStartDate(moment().subtract(30, "days").format("YYYY-MM-DD"));
    setExportEndDate(moment().format("YYYY-MM-DD"));
    setExportDialogOpen(true);
  };

  const handleExportWorkouts = async () => {
    if (!exportStartDate || !exportEndDate) {
      alert("Please provide both start and end dates.");
      return;
    }
    const startMoment = moment(exportStartDate, "YYYY-MM-DD", true);
    const endMoment = moment(exportEndDate, "YYYY-MM-DD", true);
    if (!startMoment.isValid() || !endMoment.isValid()) {
      alert("Please provide valid dates.");
      return;
    }
    if (endMoment.isBefore(startMoment, "day")) {
      alert("End date must be on or after start date.");
      return;
    }

    setExportLoading(true);
    try {
      const response = await getWorkoutsForUser(
        clientId,
        startMoment.startOf("day").unix(),
        endMoment.endOf("day").unix()
      );
      const rows = (Array.isArray(response) ? response : [])
        .sort((a, b) => (a.date || 0) - (b.date || 0))
        .map((workout) => {
          const exercises = Array.isArray(workout.exercises)
            ? workout.exercises
            : [];
          const exerciseNames = exercises
            .map((exercise) => exercise?.name)
            .filter(Boolean)
            .join(" | ");
          return [
            moment.unix(workout.date).format("YYYY-MM-DD"),
            workout.name || "",
            workout.description || "",
            workout.completed ? "Yes" : "No",
            exercises.length,
            exerciseNames,
          ];
        });

      const headers = [
        "Date",
        "Workout Name",
        "Description",
        "Completed",
        "Exercise Count",
        "Exercise Names",
      ];
      const csvLines = [headers, ...rows].map((row) =>
        row.map((value) => csvEscape(value)).join(",")
      );
      const csvContent = csvLines.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeClientName = (client?.name || "client")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      link.href = url;
      link.download = `${safeClientName || "client"}-workouts-${startMoment.format(
        "YYYYMMDD"
      )}-${endMoment.format("YYYYMMDD")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    } catch (error) {
      console.error("Failed to export workouts:", error);
      alert("Failed to export workouts. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenAssignDialog = async () => {
    setAssignDialogOpen(true);
    if (!assignPrograms.length) {
      const progs = (await getPrograms()) || [];
      setAssignPrograms(progs);
    }
    if (!assignTemplates.length) {
      const temps = (await getWorkoutTemplates()) || [];
      setAssignTemplates(temps);
    }
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssignProgramId("");
    setAssignDaySelections({});
    setAssignSaving(false);
    setAssignStartDate(moment());
  };

  const handleProgramChange = (programId) => {
    setAssignProgramId(programId);
    const program = assignPrograms.find((p) => p.id === programId);
    if (program) {
      const maxSlots = Math.max(
        0,
        ...(program.weeks || []).map((week) => week?.length || 0)
      );
      const selections = {};
      for (let slot = 0; slot < maxSlots; slot += 1) {
        selections[slot] = 1; // default Monday
      }
      setAssignDaySelections(selections);
    } else {
      setAssignDaySelections({});
    }
  };

  /** Day of week by workout slot index (0 = first workout each week); applies to all weeks. */
  const handleDayChange = (workoutSlotIndex, day) => {
    setAssignDaySelections((prev) => ({
      ...prev,
      [workoutSlotIndex]: day,
    }));
  };

  const handleAssignProgram = async () => {
    if (!assignProgramId || !assignStartDate) return;
    const program = assignPrograms.find((p) => p.id === assignProgramId);
    if (!program) return;
    setAssignSaving(true);
    const base = moment(assignStartDate).startOf("day");
    try {
      const allTemplatesMap = new Map(assignTemplates.map((t) => [t.id, t]));
      const programWeeks = program.weeks || [];
      const programLength = programWeeks.length || 1;
      for (let weekIdx = 0; weekIdx < programWeeks.length; weekIdx++) {
        const weekTemplates = programWeeks[weekIdx] || [];
        for (let i = 0; i < weekTemplates.length; i++) {
          const tid = weekTemplates[i];
          const tmpl = allTemplatesMap.get(tid);
          if (!tmpl) continue;
          const day = assignDaySelections[i] ?? 1; // Monday default; same slot across weeks
          const targetDate = base.clone().add(weekIdx, "weeks").weekday(day);
          const payload = {
            name: tmpl.name,
            description: tmpl.description,
            exercises: tmpl.exercises,
            date: targetDate.unix(),
            completed: false,
            currentWeek: weekIdx + 1,
            programLength,
          };
          await addWorkoutForUser(clientId, payload);
        }
      }
      handleCloseAssignDialog();
      fetchWorkouts();
    } catch (err) {
      console.error("Failed to assign program:", err);
      alert("Failed to assign program. Please try again.");
    } finally {
      setAssignSaving(false);
    }
  };

  const menuItems = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          minWidth: "200px",
          maxWidth: "300px",
        },
      }}
    >
      <LoadingButton
        onClick={handleRepeatLastWeek}
        loading={repeatLoading}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textTransform: "none",
          color: "inherit",
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        Repeat Last Week
      </LoadingButton>
      <LoadingButton
        onClick={() => {
          handleCloseMenu();
          handleOpenAssignDialog();
        }}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textTransform: "none",
          color: "inherit",
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        Assign Program
      </LoadingButton>
      {stripeIsSetup && (
        <LoadingButton
          onClick={handleOpenProductsDialog}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textTransform: "none",
            color: "inherit",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Manage Products
        </LoadingButton>
      )}
      <LoadingButton
        onClick={handleOpenDocumentsDialog}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textTransform: "none",
          color: "inherit",
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        Upload Documents
      </LoadingButton>
      {!isViewingSelf && (
        <LoadingButton
          onClick={handleOpenExportDialog}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textTransform: "none",
            color: "inherit",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Export Workouts (CSV)
        </LoadingButton>
      )}
      {!isViewingSelf && (
        <LoadingButton
          onClick={handleOpenEditUser}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textTransform: "none",
            color: "inherit",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Edit user
        </LoadingButton>
      )}
      {!isViewingSelf && (
        <LoadingButton
          onClick={handleResetPassword}
          loading={resetPasswordLoading}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textTransform: "none",
            color: "inherit",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Reset Password
        </LoadingButton>
      )}
      {!isViewingSelf && (
        <LoadingButton
          onClick={handleDeleteClient}
          loading={deleteLoading}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textTransform: "none",
            color: "error.main",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Remove Client
        </LoadingButton>
      )}
    </Menu>
  );

  useEffect(() => {
    if (setHeaderActions) {
      setHeaderActions(
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handleOpenMenu}>
            <MoreVertIcon />
          </IconButton>
          {menuItems}
        </Box>
      );
    }
    return () => setHeaderActions && setHeaderActions(null);
  }, [
    setHeaderActions,
    anchorEl,
    repeatLoading,
    resetPasswordLoading,
    deleteLoading,
    exportLoading,
    productsDialogOpen,
    confirmDeleteOpen,
    documentsDialogOpen,
    exportDialogOpen,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <div className="h-screen p-4">
        <Dialog
          open={confirmDeleteOpen}
          onClose={handleCloseConfirmDelete}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Remove Client</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to remove this client? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDelete}>Cancel</Button>
            <Button onClick={handleDeleteClient} color="error" autoFocus>
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={unsavedDialogOpen}
          onClose={handleCancelClose}
          aria-labelledby="unsaved-changes-title"
        >
          <DialogTitle id="unsaved-changes-title">Unsaved changes</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You have unsaved changes. Do you want to save before closing?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmSave} color="primary">
              Save
            </Button>
            <Button onClick={handleDiscardChanges} color="error">
              Close without saving
            </Button>
            <Button onClick={handleCancelClose}>Cancel</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={editUserModalOpen}
          onClose={() => !editUserSaving && setEditUserModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Edit client</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              fullWidth
              variant="outlined"
              value={editUserNameValue}
              onChange={(e) => setEditUserNameValue(e.target.value)}
              disabled={editUserSaving}
            />
            <FormControl fullWidth margin="dense" disabled={editUserSaving}>
              <InputLabel id="edit-user-weight-unit-label">
                Weight Unit
              </InputLabel>
              <Select
                labelId="edit-user-weight-unit-label"
                value={editUserWeightUnit}
                label="Weight Unit"
                onChange={(e) => setEditUserWeightUnit(e.target.value)}
              >
                <MenuItem value="lbs">Pounds (lbs)</MenuItem>
                <MenuItem value="kg">Kilograms (kg)</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense" disabled={editUserSaving}>
              <InputLabel id="edit-user-auto-review-mode-label">
                Auto-review mode
              </InputLabel>
              <Select
                labelId="edit-user-auto-review-mode-label"
                value={editUserAutoReviewMode}
                label="Auto-review mode"
                onChange={(e) => setEditUserAutoReviewMode(e.target.value)}
              >
                <MenuItem value={AUTO_REVIEW_MODES.WEIGHT_PROGRESS}>
                  Weight progression (client logs RPE)
                </MenuItem>
                <MenuItem value={AUTO_REVIEW_MODES.RPE_TARGET}>
                  RPE target (client finds weight)
                </MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditUserModalOpen(false)}
              disabled={editUserSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEditUser}
              disabled={editUserSaving || !editUserNameValue.trim()}
              variant="contained"
            >
              {editUserSaving ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={exportDialogOpen}
          onClose={() => !exportLoading && setExportDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Export workouts to CSV</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Choose a date range to export {client?.name || "client"} workouts.
            </DialogContentText>
            <TextField
              label="Start date"
              type="date"
              fullWidth
              margin="dense"
              value={exportStartDate}
              onChange={(e) => setExportStartDate(e.target.value)}
              disabled={exportLoading}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date"
              type="date"
              fullWidth
              margin="dense"
              value={exportEndDate}
              onChange={(e) => setExportEndDate(e.target.value)}
              disabled={exportLoading}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setExportDialogOpen(false)}
              disabled={exportLoading}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleExportWorkouts}
              loading={exportLoading}
              variant="contained"
            >
              Download CSV
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <AssignProgramDialog
          open={assignDialogOpen}
          onClose={handleCloseAssignDialog}
          programs={assignPrograms}
          templates={assignTemplates}
          selectedProgramId={assignProgramId}
          onProgramChange={handleProgramChange}
          startDate={assignStartDate}
          onStartDateChange={setAssignStartDate}
          daySelections={assignDaySelections}
          onDayChange={handleDayChange}
          onAssign={handleAssignProgram}
          saving={assignSaving}
        />

        <ManageProductsDialog
          open={productsDialogOpen}
          onClose={handleCloseProductsDialog}
          onSaved={onProductsSaved}
          client={client}
        />

        <UploadDocumentModal
          open={documentsDialogOpen}
          onClose={handleCloseDocumentsDialog}
          clientId={clientId}
          clientName={client?.name}
        />

        <>
          {selectedWorkoutIds.size > 0 && (
            <Box
              sx={{
                mb: 1,
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography variant="body2">
                {selectedWorkoutIds.size} workout
                {selectedWorkoutIds.size !== 1 ? "s" : ""} selected for{" "}
                <b>{selectionMode === "copy" ? "copy" : "move"}</b>.{" "}
                {selectionMode === "copy"
                  ? "Switch clients if needed, then click a date to place them."
                  : "Click a date on this client to place them."}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{ color: "white", borderColor: "white", flexShrink: 0 }}
                onClick={() => {
                  setSelectedWorkoutIds(new Set());
                  setSelectionMode(null);
                  setSelectedWorkoutsForCopy([]);
                }}
              >
                Cancel (Esc)
              </Button>
            </Box>
          )}
          <DragAndDropCalendar
            localizer={localizer}
            events={workouts}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            onNavigate={handleNavigate}
            onEventDrop={handleEventDrop}
            draggableAccessor={() => true}
            resizableAccessor={() => false}
            selectable
            style={calendarStyles}
            views={["month"]}
            defaultView="month"
            date={dateRange.start.toDate()}
            components={{
              toolbar: CalendarToolbar,
              event: ({ event }) => (
                <div style={{ position: "relative" }}>
                  <DayComponent
                    date={event.start}
                    workouts={[event]}
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectedWorkoutIds={selectedWorkoutIds}
                    selectionMode={selectionMode}
                  />
                </div>
              ),
            }}
          />

          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleAttemptCloseDrawer}
            PaperProps={{
              sx: {
                width: mobile
                  ? "100vw"
                  : selectedHistoryExercise
                  ? "80vw"
                  : "60vw",
                minWidth: mobile
                  ? "100vw"
                  : selectedHistoryExercise
                  ? "1100px"
                  : "800px",
              },
            }}
          >
            <ThemeProvider theme={theme}>
              <Box sx={{ display: "flex", height: "100%" }}>
                <Box sx={{ flex: selectedHistoryExercise ? 0.6 : 1 }}>
                  <AddWorkoutDetails
                    open={drawerOpen}
                    handleClose={handleCloseDrawer}
                    userId={clientId}
                    workout={selectedWorkout}
                    onSave={handleSaveWorkout}
                    onWorkoutCopied={fetchWorkouts}
                    onWorkoutDeleted={fetchWorkouts}
                    onWorkoutUpdated={fetchWorkouts}
                    inline={true}
                    onDirtyChange={setHasUnsavedChanges}
                    submitButtonId={saveButtonId}
                    onOpenHistoryExercise={setSelectedHistoryExercise}
                    focusExerciseIndex={notificationExerciseFocus?.index ?? null}
                    openCommentsForExerciseIndex={
                      notificationExerciseFocus?.openComments
                        ? notificationExerciseFocus?.index
                        : null
                    }
                    onNotificationFocusConsumed={() =>
                      setNotificationExerciseFocus(null)
                    }
                    defaultWeightUnit={client?.weightUnit === "kg" ? "kg" : "lbs"}
                  />
                </Box>
                {selectedHistoryExercise && (
                  <Box
                    sx={{
                      flex: 0.4,
                      minWidth: 0,
                      borderLeft: "1px solid",
                      borderColor: "divider",
                      pl: 2,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      maxHeight: "100%",
                      overflow: "auto",
                    }}
                  >
                    <History
                      exerciseName={selectedHistoryExercise}
                      currentWorkoutDate={selectedWorkout?.date}
                      close={() => setSelectedHistoryExercise(null)}
                      userId={clientId}
                      embedded
                    />
                  </Box>
                )}
              </Box>
            </ThemeProvider>
          </Drawer>
        </>
      </div>
    </ThemeProvider>
  );
};

export default ClientWorkoutCalendar;
